/** Context-source controls for test rendering — pasted JSON / Event_Delivery_Log
 *  id / entity id per event family. Extracted from TestRenderPage so the
 *  notification editor's TestRenderDialog shares the exact same UX. Pure
 *  controlled component + pure helpers (no fetching).
 */
import LinkIcon from '@mui/icons-material/Link';
import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect } from 'react';

import type { EvaluateContext } from '@/api/notificationTestRender';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

import { contextSourceActions } from '../notificationLinks';

export type SourceMode = 'json' | 'event_delivery_log' | 'entity';

export type EntityKind = 'gtu' | 'order' | 'user';

export const ENTITY_LABEL: Record<EntityKind, string> = {
  gtu: 'Tour participant (GTU) ID',
  order: 'Order ID',
  user: 'User ID',
};

export interface ContextSourceValue {
  mode: SourceMode;
  jsonText: string;
  edlId: string;
  entityId: string;
}

export const EMPTY_CONTEXT_SOURCE: ContextSourceValue = {
  mode: 'json',
  jsonText: '',
  edlId: '',
  entityId: '',
};

/** The wire context for the current source selection. */
export function buildEvaluateContext(v: ContextSourceValue): EvaluateContext {
  if (v.mode === 'event_delivery_log') {
    return { mode: 'event_delivery_log', event_delivery_log_id: v.edlId.trim() };
  }
  if (v.mode === 'entity') return { mode: 'entity', entity_id: Number(v.entityId) };
  return { mode: 'json', json: v.jsonText };
}

/** Whether the selected source has everything it needs to render. */
export function contextSourceComplete(
  v: ContextSourceValue,
  entityKind: EntityKind | null,
): boolean {
  if (v.mode === 'json') return true;
  if (v.mode === 'event_delivery_log') return v.edlId.trim() !== '';
  return entityKind !== null && v.entityId.trim() !== '';
}

/** The record the typed id names, as an end adornment. Empty or unparseable
 *  input yields no actions and renders nothing, so a half-typed id never grows
 *  a trigger that would 404. */
function SourceAdornment({ actions }: { actions: ValueAction[] }) {
  if (actions.length === 0) return null;
  return (
    <InputAdornment position="end">
      <ValueActions chip actions={actions} sx={{ color: 'text.secondary', lineHeight: 0 }}>
        <LinkIcon fontSize="small" titleAccess="Open the record this id names" />
      </ValueActions>
    </InputAdornment>
  );
}

interface Props {
  value: ContextSourceValue;
  onChange: (v: ContextSourceValue) => void;
  /** Entity kind supported by the selected event (null = entity mode disabled). */
  entityKind: EntityKind | null;
  /** Event family shown in the entity-mode helper text. */
  entityFamily?: string | undefined;
}

export function ContextSourcePicker({ value, onChange, entityKind, entityFamily }: Props) {
  const entitySupported = entityKind !== null;

  // Keep mode valid: entity is only available for events that support it.
  useEffect(() => {
    if (value.mode === 'entity' && !entitySupported) onChange({ ...value, mode: 'json' });
  }, [value, entitySupported, onChange]);

  return (
    <>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value.mode}
        onChange={(_, v: SourceMode | null) => v && onChange({ ...value, mode: v })}
      >
        <ToggleButton value="json">Paste JSON</ToggleButton>
        <ToggleButton value="event_delivery_log">Delivery log</ToggleButton>
        <ToggleButton value="entity" disabled={!entitySupported}>
          {entityKind ? ENTITY_LABEL[entityKind].replace(/ ID$/, '') : 'Entity'}
        </ToggleButton>
      </ToggleButtonGroup>

      {value.mode === 'json' && (
        <TextField
          label="Context JSON"
          size="small"
          fullWidth
          multiline
          minRows={5}
          value={value.jsonText}
          onChange={(e) => onChange({ ...value, jsonText: e.target.value })}
          placeholder={'{"guest": {"first_name": "Alice"}}'}
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
        />
      )}
      {value.mode === 'event_delivery_log' && (
        <TextField
          label="Event Delivery Log ID"
          size="small"
          fullWidth
          value={value.edlId}
          onChange={(e) => onChange({ ...value, edlId: e.target.value })}
          placeholder="UUID — its archived rendering context is fetched"
          InputProps={{
            endAdornment: (
              <SourceAdornment
                actions={contextSourceActions('event_delivery_log', value.edlId)}
              />
            ),
          }}
        />
      )}
      {value.mode === 'entity' && entitySupported && (
        <TextField
          label={entityKind ? ENTITY_LABEL[entityKind] : 'Entity ID'}
          size="small"
          type="number"
          value={value.entityId}
          onChange={(e) => onChange({ ...value, entityId: e.target.value })}
          helperText={entityFamily ? `Builds the ${entityFamily} context from this record.` : undefined}
          InputProps={{
            endAdornment: (
              <SourceAdornment actions={contextSourceActions(entityKind, value.entityId)} />
            ),
          }}
        />
      )}
    </>
  );
}
